<template>
  <v-container class="contact-page" fluid>
    <snack-bar v-model="isSaveSuccess" :content="snackbarSuccess"></snack-bar>
    <snack-bar v-model="isSaveFail" :content="snackbarFail" type="error"></snack-bar>

    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Contact Form <span class="thai">ข้อมูลการติดต่อ</span></h1>
        <p class="subhead mb-0">Manage public-facing hospital contact cards for website Contact Us page.</p>
      </div>
    </div>

    <div class="tabs mb-4">
      <button :class="['tab', {'active': isSubmissionsTab}]" @click="goToSubmissions">Submissions</button>
      <button :class="['tab', {'active': isRoutingTab}]" @click="goToRoutingTab">Email Routing Configuration</button>
      <button :class="['tab', {'active': isHospitalInfoTab}]">Hospital Contact Info</button>
    </div>

    <v-card class="table-card">
      <v-progress-linear
        v-if="isBranchListLoading || isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <div class="hospital-form-wrap" v-if="canViewHospitalInfo">
        <div class="section-block">
          <div class="section-title">Hospital Branch</div>
          <div class="section-row align-center">
            <v-select
              v-model="selectedOrgCodename"
              :items="orgItems"
              item-text="text"
              item-value="value"
              dense
              outlined
              hide-details
              class="org-select"
              :disabled="isBranchLocked || isLoading || isBranchListLoading"
              @change="onOrgChange"
            ></v-select>
            <span class="lock-note" v-if="isBranchLocked">
              <v-icon x-small>mdi-lock-outline</v-icon>
              Branch Admins are auto-locked to their own org_codename
            </span>
          </div>
        </div>

        <v-divider></v-divider>

        <div class="section-block">
          <div class="section-title">Phone Numbers</div>
          <div class="grid-2">
            <div>
              <label>Emergency / 24-HR Call Center Line</label>
              <v-text-field v-model="form.phone_emergency" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
            <div>
              <label>General Call Center / Reception Line</label>
              <v-text-field v-model="form.phone_general" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <div class="section-block">
          <div class="section-title">Digital Contact Channels</div>
          <div class="grid-2">
            <div>
              <label>General Inquiry Email</label>
              <v-text-field v-model="form.email_inquiry" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
            <div>
              <label>Line Official ID</label>
              <v-text-field v-model="form.line_official_id" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
            <div>
              <label>Line Add-Friend URL</label>
              <v-text-field v-model="form.line_official_url" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <div class="section-block">
          <div class="section-title">Physical Address & Location</div>
          <div class="grid-2">
            <div>
              <label>Physical Address (Thai)</label>
              <v-textarea v-model="form.address_th" outlined hide-details rows="3" :disabled="isLoading || isBranchListLoading"></v-textarea>
            </div>
            <div>
              <label>Physical Address (English)</label>
              <v-textarea v-model="form.address_en" outlined hide-details rows="3" :disabled="isLoading || isBranchListLoading"></v-textarea>
            </div>
            <div>
              <label>Latitude</label>
              <v-text-field v-model="form.map_latitude" dense outlined hide-details type="number" :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
            <div>
              <label>Longitude</label>
              <v-text-field v-model="form.map_longitude" dense outlined hide-details type="number" :disabled="isLoading || isBranchListLoading"></v-text-field>
            </div>
            <div class="span-2">
              <label>Google Maps Embed URL</label>
              <v-text-field v-model="form.map_embed_url" dense outlined hide-details :disabled="isLoading || isBranchListLoading"></v-text-field>
              <div class="field-note">
                Paste the full iframe "src" embed URL from Google Maps — this is rendered on the public Contact Us page.
              </div>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <div class="form-actions">
          <v-btn outlined :disabled="isLoading || isBranchListLoading" @click="loadOrganization">Cancel</v-btn>
          <v-btn color="primary" :loading="isSaving" :disabled="!selectedOrgCodename || isLoading || isBranchListLoading || !isFormDirty || !canEditHospitalInfo" @click="save">Save Changes</v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import client from '@/apis/http-client'
import SnackBar from '@/components/SnackBar'
import {hasPermision, snackbarFailContent, snackbarSuccessContent} from '@/helper/helper'

const defaultForm = () => ({
  phone_emergency: '',
  phone_general: '',
  email_inquiry: '',
  line_official_id: '',
  line_official_url: '',
  address_th: '',
  address_en: '',
  map_latitude: '',
  map_longitude: '',
  map_embed_url: ''
})

export default {
  components: {
    SnackBar
  },
  data() {
    return {
      pageName: 'hospital contact info',
      isLoading: false,
      isBranchListLoading: false,
      isSaving: false,
      isSaveSuccess: false,
      isSaveFail: false,
      snackTitle: 'updated',
      selectedOrgCodename: '',
      orgItems: [],
      baseOrganization: {},
      form: defaultForm(),
      originalForm: defaultForm(),
      breadcrumbs: [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Contact Form', disabled: true, href: '/contact-form'},
        // {text: 'Hospital Contact Info', disabled: true, href: '/contact-form/hospital-contact-info'}
      ]
    }
  },
  computed: {
    snackbarSuccess() {
      return snackbarSuccessContent(this.pageName, this.snackTitle)
    },
    snackbarFail() {
      return snackbarFailContent(this.pageName, this.snackTitle)
    },
    isSubmissionsTab() {
      return this.$route.name === 'contact-form-list'
    },
    isRoutingTab() {
      return this.$route.name === 'contact-form-routing'
    },
    isHospitalInfoTab() {
      return this.$route.name === 'contact-form-hospital-info'
    },
    isBranchLocked() {
      const user = this.$store.state.auth.user || {}
      return !user.is_superuser
    },
    canViewHospitalInfo() {
      return hasPermision('CanViewOrganizationContact') || hasPermision('CanEditOrganizationContact')
    },
    canEditHospitalInfo() {
      return hasPermision('CanEditOrganizationContact')
    },
    isFormDirty() {
      return JSON.stringify(this.formComparable(this.form)) !== JSON.stringify(this.formComparable(this.originalForm))
    }
  },
  methods: {
    formComparable(input = {}) {
      return {
        phone_emergency: input.phone_emergency || '',
        phone_general: input.phone_general || '',
        email_inquiry: input.email_inquiry || '',
        line_official_id: input.line_official_id || '',
        line_official_url: input.line_official_url || '',
        address_th: input.address_th || '',
        address_en: input.address_en || '',
        map_latitude: input.map_latitude === null || input.map_latitude === undefined ? '' : String(input.map_latitude),
        map_longitude: input.map_longitude === null || input.map_longitude === undefined ? '' : String(input.map_longitude),
        map_embed_url: input.map_embed_url || ''
      }
    },
    goToSubmissions() {
      this.$router.push({name: 'contact-form-list'}).catch(() => {})
    },
    goToRoutingTab() {
      this.$router.push({name: 'contact-form-routing'}).catch(() => {})
    },
    getUserOrgCode() {
      const user = this.$store.state.auth.user || {}
      return (user.org_codename || user.org_code || user.org || '').toLowerCase()
    },
    normalizeForm(org = {}) {
      const normalized = this.formComparable(org)
      this.baseOrganization = {
        codename: org.codename || this.selectedOrgCodename || '',
        initial: org.initial || '',
        bu_code: org.bu_code || '',
        name: org.name || org.name_en || '',
        name_th: org.name_th || '',
        image_url: org.image_url || '',
        binding_card_logo_url: org.binding_card_logo_url || '',
        is_active: org.is_active
      }
      this.form = {...normalized}
      this.originalForm = {...normalized}
    },
    async loadOrgItems() {
      this.isBranchListLoading = true
      try {
        const response = await client.secureHttpClient.get('por/api/organization/permission?permission=View&category=Organization')
        const orgs = response?.data?.data || []
        this.orgItems = orgs.map(org => ({
          text: `${org.name || org.name_th || org.codename} (${org.codename})`,
          value: org.codename
        }))
      } finally {
        this.isBranchListLoading = false
      }
    },
    async loadOrganization() {
      if (!this.selectedOrgCodename) return
      this.isLoading = true
      try {
        const response = await client.secureHttpClient.get(`por/api/mobile/organization/${this.selectedOrgCodename}`)
        const data = response?.data || {}
        this.normalizeForm(data)
      } finally {
        this.isLoading = false
      }
    },
    async onOrgChange() {
      await this.loadOrganization()
    },
    async save() {
      if (!this.selectedOrgCodename) return
      this.isSaving = true
      this.isSaveSuccess = false
      this.isSaveFail = false
      try {
        const payload = {
          ...this.baseOrganization,
          ...this.form,
          map_latitude: this.form.map_latitude === '' ? null : this.form.map_latitude,
          map_longitude: this.form.map_longitude === '' ? null : this.form.map_longitude
        }
        await client.secureHttpClient.patch(`por/api/mobile/organization/${this.selectedOrgCodename}`, payload)
        this.originalForm = {...this.formComparable(this.form)}
        this.isSaveSuccess = true
      } catch (e) {
        this.isSaveFail = true
      } finally {
        this.isSaving = false
      }
    }
  },
  async created() {
    if (!this.canViewHospitalInfo) {
      this.$router.replace({name: 'not-found'}).catch(() => {})
      return
    }

    await this.loadOrgItems()

    if (this.isBranchLocked) {
      const userOrgCode = this.getUserOrgCode()
      this.selectedOrgCodename = userOrgCode
      const filteredItems = this.orgItems.filter(item => item.value === userOrgCode)
      this.orgItems = filteredItems.length ? filteredItems : [{text: (userOrgCode || '-').toUpperCase(), value: userOrgCode}]
    } else if (this.orgItems.length > 0) {
      this.selectedOrgCodename = this.orgItems[0].value
    }

    await this.loadOrganization()
  }
}
</script>

<style scoped>
.contact-page { padding: 8px 24px 40px; }
.head-wrap { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; }
.thai { font-size:15px; color:#64748b; font-weight:500; }
.subhead { color:#64748b; font-size:13px; }
.tabs { display:flex; border-bottom:1px solid #e6e9f0; }
.tab { border:none; background:transparent; padding:10px 18px; font-weight:600; color:#64748b; cursor:pointer; }
.tab.active { color:#2f6fed; border-bottom:2px solid #2f6fed; }
.table-card { border:1px solid #e6e9f0; border-radius:10px !important; overflow:hidden; }
.hospital-form-wrap { padding:16px; }
.section-block { padding:8px 0 16px; }
.section-title { text-transform:uppercase; font-size:12px; color:#94a3b8; font-weight:800; margin-bottom:12px; letter-spacing:.5px; }
.section-row { display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.org-select { max-width:460px; min-width:320px; }
.lock-note { display:inline-flex; align-items:center; gap:6px; font-size:11.5px; color:#64748b; background:#f4f6fb; border:1px solid #e6e9f0; border-radius:7px; padding:6px 11px; }
.grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px 14px; }
.span-2 { grid-column: 1 / -1; }
label { display:block; font-size:11px; color:#64748b; text-transform:uppercase; font-weight:700; margin-bottom:6px; letter-spacing:.4px; }
.field-note { margin-top:6px; color:#94a3b8; font-size:12px; line-height:1.45; }
.form-actions { display:flex; justify-content:flex-end; gap:10px; padding-top:16px; }
</style>
